import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockIcon from "@mui/icons-material/Lock";
import PrintIcon from "@mui/icons-material/Print";
import {
  fetchChecklistEvaluation,
  fetchChecklist,
  fetchUsersByIds,
} from "../../api/backend";
import { useTheme } from "@mui/material/styles";
import { itemPoints, totalPoints } from "../../components/checklistScoring";

const DS = {
  surface: "var(--color-surface-2)",
  border: "rgba(0,0,0,0.08)",
};

// Inject print-only CSS once
const PRINT_STYLE_ID = "skill-results-print-css";
function injectPrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      /* Hide sidebar, nav, and any no-print elements */
      nav, .no-print, .MuiDrawer-root, .MuiAppBar-root { display: none !important; }
      /* Force background colors to print (section header rows, chips) */
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      /* Prevent rows from splitting across pages */
      tr { page-break-inside: avoid; }
      /* Show print-only footer */
      .print-footer { display: block !important; margin-top: 24px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
      /* Expand main content to full width (sidebar hidden) */
      main { margin-left: 0 !important; padding: 16px !important; max-width: 100% !important; }
      @page { margin: 1.5cm; }
    }
  `;
  document.head.appendChild(style);
}

export default function SkillChecklistResultsPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { id: courseId, checklistId, studentId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [evaluation, setEvaluation] = useState(state?.result ?? null);
  const [checklist, setChecklist] = useState(null);
  const [student, setStudent] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(!state?.result);

  useEffect(() => {
    injectPrintStyles();
  }, []);

  useEffect(() => {
    const tasks = [fetchChecklist(checklistId)];
    if (!evaluation)
      tasks.push(fetchChecklistEvaluation(courseId, checklistId, studentId));
    if (studentId)
      tasks.push(
        fetchUsersByIds([studentId])
          .then((u) => u?.[0] ?? null)
          .catch(() => null),
      );

    Promise.all(tasks).then((results) => {
      setChecklist(results[0]);
      const ev = evaluation ?? results[1];
      if (!evaluation) setEvaluation(ev);
      if (studentId) setStudent(results[evaluation ? 1 : 2]);
      // Fetch instructor name if not stored on evaluation
      if (ev?.instructorId && !ev?.examinerName) {
        fetchUsersByIds([ev.instructorId])
          .then((u) => setInstructor(u?.[0] ?? null))
          .catch(() => null);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, checklistId, studentId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!evaluation) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", p: 4 }}>
        <Alert severity="warning">No evaluation found for this student.</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={() => navigate(`/course-management/${courseId}/checklists`)}
        >
          Back to Skill Check Sheets
        </Button>
      </Box>
    );
  }

  const isPassed = evaluation.finalResult === "PASS";
  const isFailed = evaluation.finalResult === "FAIL";

  // Resolve examiner display name: stored field → fetched instructor → fallback
  const resolvedExaminerName =
    evaluation.examinerName ||
    (instructor ? (instructor.fullName || `${instructor.firstName ?? ""} ${instructor.lastName ?? ""}`.trim()) : null) ||
    null;

  const items = Array.isArray(checklist?.items) ? checklist.items : [];
  const instructorResults = Array.isArray(evaluation.instructorItemResults)
    ? evaluation.instructorItemResults
    : [];

  const resultByItemId = {};
  instructorResults.forEach((r) => {
    resultByItemId[r.id] = r;
  });

  const pointsAwarded =
    evaluation.pointsAwarded ??
    instructorResults.reduce((s, r) => s + (r.pointsAwarded ?? 0), 0);

  // Total possible = sum of item points on the checklist
  const totalPossible = items.length > 0
    ? totalPoints(items)
    : instructorResults.length;

  const gradePercent =
    evaluation.gradePercent ??
    (totalPossible > 0
      ? Math.round((pointsAwarded / totalPossible) * 1000) / 10
      : null);

  const passingScore = checklist?.passingScore ?? null;

  const studentName = student
    ? `${student.firstName ?? ""} ${student.lastName ?? ""}`.trim()
    : `Student ${studentId}`;
  const initials = studentName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Group items by sectionTitle
  const itemsToRender = items.length > 0 ? items : instructorResults;
  const sectionMap = new Map();
  itemsToRender.forEach((item) => {
    const sec = item.sectionTitle || "General Skills";
    if (!sectionMap.has(sec)) sectionMap.set(sec, []);
    sectionMap.get(sec).push(item);
  });
  const sections = Array.from(sectionMap.entries()); // [[title, items[]], ...]

  const handlePrint = () => window.print();

  return (
    <Box id="skill-results-root" sx={{ maxWidth: 900, mx: "auto" }}>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2.5 }}
        className="no-print"
      >
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => navigate("/course-management")}
        >
          Course Management
        </Link>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => navigate(`/course-management/${courseId}/checklists`)}
        >
          Skill Check Sheets
        </Link>
        <Typography
          color="text.primary"
          sx={{ fontSize: "0.85rem", fontWeight: 600 }}
        >
          Assessment Results
        </Typography>
      </Breadcrumbs>

      {/* Printable area */}
      <Box>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Avatar
            sx={{ width: 44, height: 44, bgcolor: tPrimary, fontSize: "1rem", mt: 0.25 }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
              {checklist?.title ?? "Skill Sheet Results"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {studentName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {evaluation.locked && (
              <Chip
                icon={<LockIcon sx={{ fontSize: 14 }} />}
                label="Signed Off & Locked"
                size="small"
                sx={{ bgcolor: "var(--color-surface-2)", color: "var(--color-ink-2)", fontWeight: 600 }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              className="no-print"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: tPrimary,
                color: tPrimary,
                "&:hover": { bgcolor: "var(--color-surface-2)" },
              }}
            >
              Print
            </Button>
          </Box>
        </Box>

        {/* Result badge */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: `1px solid ${DS.border}`,
            p: 3,
            mb: 3,
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Box sx={{ textAlign: "center", minWidth: 120 }}>
            {isPassed ? (
              <CheckCircleIcon sx={{ fontSize: 56, color: "var(--color-success)" }} />
            ) : isFailed ? (
              <CancelIcon sx={{ fontSize: 56, color: "var(--color-danger)" }} />
            ) : (
              <WarningAmberIcon sx={{ fontSize: 56, color: "var(--color-warn)" }} />
            )}
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: isPassed ? "var(--color-success)" : isFailed ? "var(--color-danger)" : "var(--color-warn)", mt: 0.5, fontFamily: "var(--font-display)" }}
            >
              {isPassed ? "PASS" : isFailed ? "FAIL" : "PENDING"}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ flex: 1, display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                Points Awarded
              </Typography>
              <Typography variant="h4" fontWeight={800} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
                {pointsAwarded}
                <Typography component="span" variant="h6" color="text.secondary" fontWeight={400}>
                  {" "}/ {totalPossible}
                </Typography>
              </Typography>
            </Box>
            {gradePercent !== null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                  Grade
                </Typography>
                <Typography variant="h4" fontWeight={800} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
                  {gradePercent}%
                </Typography>
              </Box>
            )}
            {passingScore !== null && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                  Pass Threshold
                </Typography>
                <Typography variant="h4" fontWeight={800} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
                  {passingScore}
                  <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                    {" "}pts
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>

          {(resolvedExaminerName || evaluation.examinationDate) && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                  Examiner
                </Typography>
                {resolvedExaminerName && (
                  <Typography variant="body2" fontWeight={600}>{resolvedExaminerName}</Typography>
                )}
                {evaluation.examinationDate && (
                  <Typography variant="caption" color="text.secondary">{evaluation.examinationDate}</Typography>
                )}
                {evaluation.assessmentLocation && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {evaluation.assessmentLocation}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Critical failure notes */}
        {evaluation.criticalFailureNotes && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: "10px" }}>
            <strong>Critical criteria not fully awarded:</strong>{" "}
            {evaluation.criticalFailureNotes.replace(/^Critical items not awarded full points:\s*/i, "")}
          </Alert>
        )}

        {/* Items table — grouped by section */}
        <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, overflow: "hidden", mb: 3 }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DS.border}` }}>
            <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
              Item Breakdown
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: DS.surface }}>
                <TableCell sx={{ width: 40, fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>CRITERIA</TableCell>
                <TableCell sx={{ width: 80, textAlign: "center", fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>POSSIBLE</TableCell>
                <TableCell sx={{ width: 80, textAlign: "center", fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>AWARDED</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.map(([sectionTitle, sectionItems]) => {
                const sectionPossible = totalPoints(sectionItems);
                const sectionAwarded = sectionItems.reduce((s, item) => {
                  const r = resultByItemId[item.id];
                  return s + (r?.pointsAwarded ?? 0);
                }, 0);
                // global index offset for this section
                const sectionOffset = itemsToRender.indexOf(sectionItems[0]);

                return (
                  <React.Fragment key={sectionTitle}>
                    {/* Section header row */}
                    <TableRow className="section-header-row">
                      <TableCell
                        colSpan={4}
                        sx={{
                          bgcolor: "#EEF2F7",
                          borderTop: `1px solid ${DS.border}`,
                          py: 0.75,
                          px: 2.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: tPrimary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {sectionTitle}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", fontWeight: 600 }}>
                            {sectionAwarded} / {sectionPossible} pts
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Items in section */}
                    {sectionItems.map((item, localIdx) => {
                      const globalIdx = sectionOffset + localIdx;
                      const r = resultByItemId[item.id];
                      const awarded = r?.pointsAwarded ?? null;
                      const possible = itemPoints(item);
                      const isCritical = item.isCritical ?? r?.isCritical ?? false;
                      const missedCritical = isCritical && awarded === 0;
                      return (
                        <TableRow
                          key={item.id ?? globalIdx}
                          sx={{
                            bgcolor: missedCritical ? "var(--color-warn-bg)" : awarded > 0 ? "var(--color-success-bg)" : undefined,
                          }}
                        >
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                            {globalIdx + 1}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                              {isCritical && (
                                <WarningAmberIcon sx={{ fontSize: 14, color: "var(--color-warn)", flexShrink: 0, mt: "2px" }} />
                              )}
                              <Box>
                                <Typography variant="body2">{item.criteria}</Typography>
                                {r?.note && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    Note: {r.note}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">{possible}</Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            {awarded === null ? (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            ) : (
                              <Chip
                                label={awarded}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  minWidth: 32,
                                  bgcolor: awarded > 0 ? "var(--color-success-bg)" : "var(--color-danger-bg)",
                                  color: awarded > 0 ? "#15803d" : "var(--color-danger)",
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {/* Totals */}
              <TableRow sx={{ bgcolor: DS.surface }}>
                <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: "0.8rem" }}>Total</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 700, fontSize: "0.8rem" }}>{totalPossible}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Chip
                    label={`${pointsAwarded} / ${totalPossible}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      bgcolor: isPassed ? "var(--color-success-bg)" : isFailed ? "var(--color-danger-bg)" : "var(--color-surface-2)",
                      color: isPassed ? "#15803d" : isFailed ? "var(--color-danger)" : "var(--color-ink-2)",
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {(evaluation.instructorOutcome || evaluation.automaticResult || evaluation.peerOverrideReason) && <Paper elevation={0} sx={{ borderRadius: "12px", border: '1px solid ' + DS.border, p: 2.5, mb: 3 }}>
          {evaluation.instructorOutcome && <Typography variant="subtitle2" fontWeight={700}>Instructor outcome: {evaluation.instructorOutcome === "SUCCESSFUL" ? "Successful" : "Unsuccessful"}</Typography>}
          <Typography variant="body2">Automatic result: {evaluation.automaticResult || "Not recorded"}</Typography>
          {evaluation.instructorComment && <Typography variant="body2">Comment: {evaluation.instructorComment}</Typography>}
          {evaluation.peerOverrideReason && <Typography variant="body2">Peer override reason: {evaluation.peerOverrideReason}</Typography>}
        </Paper>}
        {/* Instructor notes */}
        {evaluation.explanation && evaluation.explanation !== evaluation.instructorComment && (
          <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, p: 2.5, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color={tPrimary} gutterBottom sx={{ fontFamily: "var(--font-display)" }}>
              Instructor Notes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {evaluation.explanation}
            </Typography>
          </Paper>
        )}

        {/* Print footer (only visible when printing) */}
        <Box className="print-footer" sx={{ display: "none" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
            Signed off by {resolvedExaminerName || "Instructor"} · {evaluation.examinationDate || ""} · Generated {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 1.5 }} className="no-print">
        <Button
          variant="outlined"
          onClick={() => navigate(`/course-management/${courseId}/checklists`)}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
        >
          Back to Skill Check Sheets
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/course-management/${courseId}/checklists/${checklistId}/assess`)}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
        >
          Assess Another Student
        </Button>
      </Box>
    </Box>
  );
}
